import { FC, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Box, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { LeagueFilter, Period } from 'models';

import { useLazyGetLeaderboardByIdQuery, useLazyGetLeaderboardByPeriodQuery } from 'api/leaderboard';

import LeaderboardFilter from 'components/leaderboard/LeaderboardFilter';
import LeaderboardPeriod from 'components/leaderboard/LeaderboardPeriod';
import LeaderboardPeriodHeader from 'components/leaderboard/LeaderboardPeriodHeader';
import LeaderboardSkeleton from 'components/leaderboard/LeaderboardSkeleton';
import LeaderboardFriendsFilter from 'components/leaderboard/leaderboardFriendsFilter';

import { useAppSelector } from 'hooks/app';

const LeaderboardPeriodPage: FC = () => {
  const isAuthicated = useAppSelector((store) => store.auth.isAuthicated);
  const { period, date } = useParams<{ period: Period; league: LeagueFilter; date: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsLeaderboardId = searchParams.get('leaderboardId');
  const searchParamsFriends = searchParams.get('friends');
  const searchParamsLeague = searchParams.get('league') as LeagueFilter;
  const navigate = useNavigate();

  if (period !== 'weekly' && period !== 'monthly') {
    navigate('/');
    return null;
  }

  if (!date) {
    const currentDate = dayjs().format('YYYY-MM-DD');
    navigate(
      `/leaderboard/${period}/${currentDate}?friends=${searchParamsFriends || 'false'}&league=${searchParamsLeague || 'all'}`,
      { replace: true }
    );
    return null;
  }

  const [getPeriodStat, { data: periodStat, isFetching: isPeriodFetching, isLoading: isPeriodLoading }] =
    useLazyGetLeaderboardByPeriodQuery();

  const [
    getLeaderboardById,
    { data: leaderboardByIdStat, isFetching: isLeaderboardByIdFetching, isLoading: isLeaderboardByIdLoading },
  ] = useLazyGetLeaderboardByIdQuery();

  useEffect(() => {
    if (searchParamsLeaderboardId) {
      getLeaderboardById({
        leaderboardId: parseInt(searchParamsLeaderboardId),
        friends: searchParamsFriends === 'true',
      });
    } else {
      getPeriodStat({
        period: period!,
        date: date,
        friends: searchParamsFriends === 'true',
        league: searchParamsLeague === 'all' ? undefined : searchParamsLeague,
      });
    }
  }, [period, searchParamsLeaderboardId, searchParamsFriends, date]);

  const selectedLeaderboardId = useMemo(() => {
    if (periodStat && searchParamsLeague && searchParamsLeague !== 'all') {
      return periodStat.leaderboards[searchParamsLeague]?.id;
    } else if (periodStat && searchParamsLeague === 'all') {
      return periodStat.leaderboards['null']?.id;
    }
    return null;
  }, [periodStat, searchParamsLeague]);

  useEffect(() => {
    if (selectedLeaderboardId) {
      getLeaderboardById({
        leaderboardId: selectedLeaderboardId,
        friends: searchParamsFriends === 'true',
      });
    }
  }, [selectedLeaderboardId, searchParamsFriends, getLeaderboardById]);

  useEffect(() => {
    if (periodStat && selectedLeaderboardId) {
      getLeaderboardById({
        leaderboardId: selectedLeaderboardId,
        friends: searchParamsFriends === 'true',
      });
    }
  }, [searchParamsLeague, periodStat, selectedLeaderboardId, searchParamsFriends, getLeaderboardById]);

  const stat = leaderboardByIdStat;
  const isFetching = isLeaderboardByIdFetching || isPeriodFetching;
  const isLoading = isLeaderboardByIdLoading || isPeriodLoading;

  const shouldShowData = stat && !isFetching && !isPeriodLoading && selectedLeaderboardId;
  const shouldShowNoData =
    (!stat && !isFetching && !isLoading) || (!selectedLeaderboardId && periodStat && !isPeriodLoading);

  const handleChangePeriodDate = (newDate: number | string) => {
    if (typeof newDate === 'string') {
      navigate(
        `/leaderboard/${period}/${newDate}?friends=${searchParamsFriends || 'false'}&league=${searchParamsLeague || 'all'}`
      );
    }
  };

  const handleChangeLeague = (league: LeagueFilter) => {
    navigate(`/leaderboard/${period}/${date}?friends=${searchParamsFriends || 'false'}&league=${league}`);
  };

  const handleChangePeriod = (newPeriod: Period) => {
    const now = dayjs().tz('America/Los_Angeles').format('YYYY-MM-DD');
    if (newPeriod !== 'daily') {
      navigate(
        `/leaderboard/${newPeriod}/${now}?friends=${searchParamsFriends || 'false'}&league=${searchParamsLeague || 'all'}`
      );
    } else if (newPeriod === 'daily') {
      navigate(`/leaderboard?friends=${searchParamsFriends || 'false'}&league=${searchParamsLeague || 'all'}`);
    }
  };

  const isDateNavigation = !searchParamsLeaderboardId;

  return (
    <Box display="flex" flexDirection="column" h="100%" gridRow="span 2">
      {isAuthicated ? (
        <LeaderboardFriendsFilter />
      ) : (
        <Text textAlign="left" variant="pageTitle">
          leaderboard
        </Text>
      )}

      <LeaderboardFilter
        selectedLeague={searchParamsLeague}
        selectedPeriod={period}
        onLeagueChange={handleChangeLeague}
        onPeriodChange={handleChangePeriod}
        stat={stat || undefined}
        date={date}
      />
      {isFetching && <LeaderboardSkeleton />}
      {shouldShowData && (
        <>
          <LeaderboardPeriod
            key={`${selectedLeaderboardId || 'period'}-${date}-${searchParamsLeague}`}
            stat={stat}
            showLeaderboard={!!stat}
            showMySquad={!!stat.me && stat.me.place >= 20}
            // showBanner={!!stat.me && period === 'monthly'}
            showBanner={false}
          />
        </>
      )}
      {shouldShowNoData && (
        <Box p="20px" textAlign="center" color="white" opacity={0.6} flex="1">
          No leaderboard data available
        </Box>
      )}
      <LeaderboardPeriodHeader
        disabled={isLoading || isPeriodLoading}
        stat={stat || undefined}
        onDateChange={handleChangePeriodDate}
        type={period}
        isDateNavigation={isDateNavigation}
        periodStat={periodStat}
      />
    </Box>
  );
};
export default LeaderboardPeriodPage;
